import { DateTime } from "luxon";
import { getRaw } from "./storage";
import { DEFAULTS, PREF, type TimeFormat, type WeekStart } from "./prefs";

/** Local calendar date string, YYYY-MM-DD. Never converted through UTC. */
export type LocalDate = string;

// ---------- display preferences (Settings → Appearance; device-local) ----------

/** Luxon weekday the calendar starts on: 1 Monday, 7 Sunday. */
export type WeekStartDay = 1 | 7;
let displayOverride: { weekStart?: WeekStartDay; hour12?: boolean } | null = null;

/** Tests pin the display preferences instead of reading storage and the host locale. */
export function setDisplayOverride(o: typeof displayOverride) {
  displayOverride = o;
}

function readPref<T extends string>(key: string, fallback: T): T {
  const raw = getRaw(key);
  if (raw === null) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

/** The locale's first day of the week when it is Sunday or Monday; Monday otherwise. */
export function systemWeekStart(): WeekStartDay {
  try {
    const loc = new Intl.Locale(typeof navigator === "undefined" ? "en-US" : navigator.language) as Intl.Locale & {
      getWeekInfo?: () => { firstDay: number };
      weekInfo?: { firstDay: number };
    };
    return (loc.getWeekInfo?.() ?? loc.weekInfo)?.firstDay === 7 ? 7 : 1;
  } catch {
    return 1;
  }
}

export function systemUses12Hour(): boolean {
  const cycle = new Intl.DateTimeFormat(undefined, { hour: "numeric" }).resolvedOptions().hourCycle;
  return cycle === "h12" || cycle === "h11";
}

export function weekStartDay(): WeekStartDay {
  if (displayOverride?.weekStart) return displayOverride.weekStart;
  const pref = readPref<WeekStart>(PREF.weekStart, DEFAULTS.weekStart);
  return pref === "sunday" ? 7 : pref === "monday" ? 1 : systemWeekStart();
}

export function uses12Hour(): boolean {
  if (displayOverride?.hour12 !== undefined) return displayOverride.hour12;
  const pref = readPref<TimeFormat>(PREF.timeFormat, DEFAULTS.timeFormat);
  return pref === "12h" ? true : pref === "24h" ? false : systemUses12Hour();
}

/** "3:30 PM" / "15:30". `short` drops ":00" on the hour in 12-hour time ("9 AM"). */
export function formatTime(dt: DateTime, opts: { short?: boolean } = {}): string {
  if (!uses12Hour()) return dt.toFormat("HH:mm");
  return dt.toFormat(opts.short && dt.minute === 0 ? "h a" : "h:mm a");
}

export function localZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

export function nowIn(tz: string): DateTime {
  const dt = DateTime.now().setZone(tz);
  return dt.isValid ? dt : DateTime.now();
}

export function todayIn(tz: string, now: DateTime = nowIn(tz)): LocalDate {
  return now.setZone(tz).toISODate()!;
}

export function parseLocalDate(date: LocalDate, tz: string): DateTime {
  return DateTime.fromISO(date, { zone: tz }).startOf("day");
}

/** Local calendar date of an instant in tz. */
export function localDateOf(iso: string, tz: string): LocalDate {
  const dt = DateTime.fromISO(iso).setZone(tz);
  return dt.isValid ? dt.toISODate()! : iso.slice(0, 10);
}

export function addDays(date: LocalDate, days: number): LocalDate {
  return DateTime.fromISO(date, { zone: "UTC" }).plus({ days }).toISODate()!;
}

/** Whole days from `from` to `to` (both local dates). */
export function daysBetween(from: LocalDate, to: LocalDate): number {
  const a = DateTime.fromISO(from, { zone: "UTC" });
  const b = DateTime.fromISO(to, { zone: "UTC" });
  return Math.round(b.diff(a, "days").days);
}

/** Monday of the following week. When today is Monday, that is 7 days later. */
export function nextMondayDate(today: LocalDate): LocalDate {
  const d = DateTime.fromISO(today, { zone: "UTC" });
  const delta = (8 - d.weekday) % 7 || 7;
  return d.plus({ days: delta }).toISODate()!;
}

/** Saturday of this week; today when it is already the weekend. */
export function thisWeekendDate(today: LocalDate): LocalDate {
  const d = DateTime.fromISO(today, { zone: "UTC" });
  if (d.weekday >= 6) return today;
  return d.plus({ days: 6 - d.weekday }).toISODate()!;
}

export type SnoozePresetKey = "1h" | "3h" | "tomorrow" | "nextWeek";
export interface SnoozePreset {
  key: SnoozePresetKey;
  label: string;
  at: DateTime;
}

/**
 * Snooze presets per PRD §7, computed in the user's timezone through luxon so DST is handled
 * by the zone database: In 1 hour, In 3 hours, Tomorrow 9 AM, Next Monday 9 AM.
 */
export function snoozePresets(tz: string, now: DateTime = nowIn(tz)): SnoozePreset[] {
  const local = now.setZone(tz);
  const today = local.toISODate()!;
  const at9 = (date: LocalDate) => DateTime.fromISO(`${date}T09:00`, { zone: tz });
  return [
    { key: "1h", label: "In 1 hour", at: local.plus({ hours: 1 }) },
    { key: "3h", label: "In 3 hours", at: local.plus({ hours: 3 }) },
    { key: "tomorrow", label: "Tomorrow", at: at9(addDays(today, 1)) },
    { key: "nextWeek", label: "Next week", at: at9(nextMondayDate(today)) },
  ];
}

export type RowSnoozeKey = "later" | "tomorrow" | "weekend" | "nextWeek";
export interface RowSnoozeOption {
  key: RowSnoozeKey;
  label: string;
  at: DateTime;
}

/**
 * Snooze choices for a task row's clock menu (owner, October 15): Later today (+3h, only while that
 * is still today), Tomorrow 9 AM, This weekend (Saturday 9 AM; "Next weekend" once it has started),
 * and Next week (Monday 9 AM). "Pick a day…" is added by the menu itself.
 */
export function rowSnoozeOptions(tz: string, now: DateTime = nowIn(tz)): RowSnoozeOption[] {
  const local = now.setZone(tz);
  const today = local.toISODate()!;
  const at9 = (date: LocalDate) => DateTime.fromISO(`${date}T09:00`, { zone: tz });
  const out: RowSnoozeOption[] = [];
  const later = local.plus({ hours: 3 });
  if (later.toISODate() === today) out.push({ key: "later", label: "Later today", at: later });
  out.push({ key: "tomorrow", label: "Tomorrow", at: at9(addDays(today, 1)) });
  const saturday = thisWeekendDate(today);
  // Sunday, or Saturday after 9 AM: this weekend is already here, so offer the next one.
  const weekendStarted = local.weekday === 7 || at9(saturday) <= local;
  out.push({
    key: "weekend",
    label: weekendStarted ? "Next weekend" : "This weekend",
    at: at9(weekendStarted ? thisWeekendDate(nextMondayDate(today)) : saturday),
  });
  out.push({ key: "nextWeek", label: "Next week", at: at9(nextMondayDate(today)) });
  return out;
}

/** Short wake time for menus and toasts: "6:30 PM", "Tomorrow 9 AM", "Sat 9 AM", "Oct 27, 9 AM". */
export function formatWake(iso: string, tz: string, now: DateTime = nowIn(tz)): string {
  const dt = DateTime.fromISO(iso).setZone(tz);
  if (!dt.isValid) return iso;
  const time = formatTime(dt, { short: true });
  const diff = daysBetween(now.setZone(tz).toISODate()!, dt.toISODate()!);
  if (diff === 0) return time;
  if (diff === 1) return `Tomorrow ${time}`;
  if (diff > 1 && diff < 7) return `${dt.toFormat("ccc")} ${time}`;
  return `${dt.toFormat(dt.year === now.year ? "LLL d" : "LLL d, yyyy")}, ${time}`;
}

/** Custom snooze from a local date + "HH:mm" in tz. */
export function customInstant(date: LocalDate, time: string, tz: string): DateTime {
  return DateTime.fromISO(`${date}T${time}`, { zone: tz });
}

export function toIsoUtc(dt: DateTime): string {
  return dt.toUTC().toISO({ suppressMilliseconds: true })!;
}

/** "Today", "Tomorrow", "Yesterday", weekday within the next 6 days, else "Oct 21" (+ year if different). */
export function formatDay(date: LocalDate, today: LocalDate, opts: { long?: boolean } = {}): string {
  const diff = daysBetween(today, date);
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff === -1) return "Yesterday";
  const d = DateTime.fromISO(date, { zone: "UTC" });
  if (diff > 1 && diff < 7) return d.toFormat(opts.long ? "cccc" : "ccc");
  const sameYear = d.year === DateTime.fromISO(today, { zone: "UTC" }).year;
  return d.toFormat(sameYear ? (opts.long ? "cccc, LLL d" : "LLL d") : "LLL d, yyyy");
}

/** Human time for an instant relative to today in tz: "Today 3:00 PM", "Tomorrow 9:00 AM", "Mon, Oct 20, 9:00 AM". */
export function formatInstant(iso: string, tz: string, now: DateTime = nowIn(tz)): string {
  const dt = DateTime.fromISO(iso).setZone(tz);
  if (!dt.isValid) return iso;
  const today = now.setZone(tz).toISODate()!;
  const date = dt.toISODate()!;
  const time = formatTime(dt);
  const diff = daysBetween(today, date);
  if (diff === 0) return `Today ${time}`;
  if (diff === 1) return `Tomorrow ${time}`;
  if (diff > 1 && diff < 7) return `${dt.toFormat("cccc")} ${time}`;
  const sameYear = dt.year === now.year;
  return `${dt.toFormat(sameYear ? "ccc, LLL d" : "LLL d, yyyy")}, ${time}`;
}

export function formatTimestamp(iso: string, tz: string): string {
  const dt = DateTime.fromISO(iso).setZone(tz);
  return dt.isValid ? `${dt.toFormat("LLL d, yyyy")} at ${formatTime(dt)}` : iso;
}

export type DeadlineState = "overdue" | "today" | "soon" | "later";
export function deadlineState(deadline: LocalDate, today: LocalDate): DeadlineState {
  if (deadline < today) return "overdue";
  if (deadline === today) return "today";
  return daysBetween(today, deadline) <= 3 ? "soon" : "later";
}

/**
 * Month grid covering the month of `month` (YYYY-MM-01), weeks starting on `weekStart`. Column order
 * only; "Next week" snoozes still mean next Monday.
 */
export function monthGrid(month: LocalDate, weekStart: WeekStartDay = weekStartDay()): LocalDate[][] {
  const first = DateTime.fromISO(month, { zone: "UTC" }).startOf("month");
  const start = first.minus({ days: (first.weekday - weekStart + 7) % 7 });
  const weeks: LocalDate[][] = [];
  let cursor = start;
  for (let w = 0; w < 6; w++) {
    const week: LocalDate[] = [];
    for (let d = 0; d < 7; d++) {
      week.push(cursor.toISODate()!);
      cursor = cursor.plus({ days: 1 });
    }
    weeks.push(week);
    if (cursor.month !== first.month && cursor > first.endOf("month")) break;
  }
  return weeks;
}

/** One-letter weekday headers in grid order. */
export function weekdayInitials(weekStart: WeekStartDay = weekStartDay()): string[] {
  const monday = ["M", "T", "W", "T", "F", "S", "S"];
  return weekStart === 7 ? ["S", ...monday.slice(0, 6)] : monday;
}

export function monthLabel(month: LocalDate): string {
  return DateTime.fromISO(month, { zone: "UTC" }).toFormat("LLLL yyyy");
}

export function shiftMonth(month: LocalDate, delta: number): LocalDate {
  return DateTime.fromISO(month, { zone: "UTC" }).startOf("month").plus({ months: delta }).toISODate()!;
}

/** Snooze Undo target: the wake time that was set before, or null (wake now) if there was none or it has passed. */
export function wakeToRestore(previous: string | null | undefined, nowIso = new Date().toISOString()): string | null {
  return previous && previous > nowIso ? previous : null;
}
